import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import fullstoryService from '../services/fullstoryService';

/**
 * RouteTracker component that tracks page views in Fullstory
 * Should be placed high in the component tree to track all route changes
 */
const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    const pageName = getPageNameFromPath(location.pathname);
    const pageProperties = getPageProperties(location.pathname, location.search);

    fullstoryService.trackPageView(pageName, pageProperties);
  }, [location.pathname, location.search]);

  return null; // This component doesn't render anything
};

/**
 * Extract meaningful page name from path
 * @param {string} pathname - The current pathname
 * @returns {string} Human-readable page name
 */
function getPageNameFromPath(pathname) {
  const pathSegments = pathname.split('/').filter(Boolean);

  // Handle specific routes with meaningful names
  if (pathname === '/') return 'Home';
  if (pathname === '/Home') return 'Landing Page';

  // Authentication routes
  if (pathname.includes('/login')) return 'Login Page';
  if (pathname.includes('/register')) return 'Registration Page';
  if (pathname.includes('/verify')) return 'Email Verification';
  if (pathname.includes('/reset')) return 'Password Reset';
  if (pathname.includes('/demo-access')) return 'Demo Access';

  // Landing page routes
  if (pathname.includes('/plans')) return 'Pricing Plans';
  if (pathname.includes('/features')) return 'Features Page';
  if (pathname.includes('/contact-us')) return 'Contact Us';
  if (pathname.includes('/blogs')) return 'Blog Page';
  if (pathname.includes('/privacy-and-policy')) return 'Privacy Policy';
  if (pathname.includes('/terms-and-conditions')) return 'Terms and Conditions';

  // User dashboard routes
  if (pathname.startsWith('/User')) {
    if (pathname === '/User') return 'User Dashboard';
    if (pathname.includes('/Dashboard')) return 'Main Dashboard';
    if (pathname.includes('/Business')) return 'Business Overview';
    if (pathname.includes('/Scenario')) return 'Scenario Planning';
    if (pathname.includes('/Financials-setup')) return 'Financial Setup';
    if (pathname.includes('/Financials-data')) return 'Financial Data Entry';
    if (pathname.includes('/business-setup')) return 'Business Setup';
    if (pathname.includes('/Subscription')) return 'Subscription Management';
    if (pathname.includes('/Reports')) return 'Financial Reports';
    if (pathname.includes('/Settings')) return 'User Settings';
    if (pathname.includes('/Overview')) return 'Reports Overview';
    if (pathname.includes('/Onboarding')) return 'User Onboarding';
    if (pathname.includes('/integrations')) return 'Integrations';
    if (pathname.includes('/Home')) return 'Main Dashboard';
  }

  // Admin routes
  if (pathname.startsWith('/Admin')) {
    if (pathname === '/Admin') return 'Admin Dashboard';
    if (pathname.includes('/Users')) return 'User Management';
    if (pathname.includes('/Financials')) return 'Financial Templates';
    if (pathname.includes('/Prompts')) return 'AI Prompts Management';
    if (pathname.includes('/Blogs')) return 'Blog Management';
  }

  // Moderator routes
  if (pathname.startsWith('/Moderator')) {
    if (pathname === '/Moderator') return 'Moderator Dashboard';
    if (pathname.includes('/Users')) return 'Client Management';
    if (pathname.includes('/Financials')) return 'Financial Review';
    if (pathname.includes('/Prompts')) return 'Prompt Review';
    if (pathname.includes('/Blogs')) return 'Blog Moderation';
  }

  // Fallback: create readable name from path segments
  return pathSegments
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '))
    .join(' - ') || 'Home';
}

/**
 * Extract additional page properties from path and search params
 * @param {string} pathname - The current pathname
 * @param {string} search - The search string
 * @returns {Object} Page properties object
 */
function getPageProperties(pathname, search) {
  const properties = {};

  // Extract query parameters
  if (search) {
    const searchParams = new URLSearchParams(search);
    for (const [key, value] of searchParams.entries()) {
      properties[`query_${key}`] = value;
    }
  }

  // Add role-based context
  if (pathname.startsWith('/User')) {
    properties.section = 'User Dashboard';
    properties.userType = 'Client';
  } else if (pathname.startsWith('/Admin')) {
    properties.section = 'Admin Panel';
    properties.userType = 'Administrator';
  } else if (pathname.startsWith('/Moderator')) {
    properties.section = 'Moderator Panel';
    properties.userType = 'Moderator';
  } else {
    properties.section = 'Public Pages';
    properties.userType = 'Visitor';
  }

  // Add timestamp for analytics
  properties.viewTimestamp = new Date().toISOString();

  return properties;
}

export default RouteTracker;
